import { cn } from "@/lib/utils";

type FranklinLogoSize = "xs" | "sm" | "md" | "lg" | "xl";

interface FranklinLogoProps {
  /** Size preset for the logo container (default: "md") */
  size?: FranklinLogoSize;
  /** Optional click handler */
  onClick?: () => void;
  /** Additional CSS classes for the container */
  className?: string;
  /** Whether to show the border (default: true) */
  showBorder?: boolean;
  /** Whether to show the shadow (default: false) */
  showShadow?: boolean;
  /** Custom border color (default: "#ffd6a7" orange) */
  borderColor?: string;
  /** Additional CSS classes for the image element (e.g., for filters) */
  imageClassName?: string;
  /** Whether to show the animated loading media instead of the static logo (default: false) */
  animated?: boolean;
  /**
   * Text announced for the mark. Pass `""` where the mark is decorative —
   * surroundings that already name the speaker (a chat bubble's gutter) would
   * otherwise make a screen reader read "Franklin, image" before every line.
   * (default: "Franklin")
   */
  alt?: string;
  /** When true, render the full Franklin wordmark (mark + brand-font "Franklin" text) instead of just the circular mark (default: false) */
  expanded?: boolean;
}

const STATIC_LOGO_SRC = "/franklin-logo.svg";
const LOADING_ANIMATION_SRC = "/franklin-chatbot-loading-animation.gif";
const WORDMARK_LOGO_SRC = "/franklin-logo-full.svg";

/**
 * Size configuration for the Franklin logo.
 *
 * - `container`: outer circular wrapper used for the standalone mark.
 * - `icon`: inner mark image — the *visible* mark size in collapsed state.
 * - `height`: wordmark height for the expanded variant. Matches the visible
 *   icon height so the mark portion of the wordmark renders at the same
 *   pixel height as the standalone mark, keeping the icon visually unchanged
 *   between collapsed and expanded states.
 */
const sizeConfig: Record<FranklinLogoSize, { container: string; icon: string; height: string }> = {
  xs: { container: "h-6 w-6", icon: "h-4 w-4", height: "h-4" },
  sm: { container: "h-8 w-8", icon: "h-5 w-5", height: "h-5" },
  md: { container: "h-10 w-10", icon: "h-6 w-6", height: "h-6" },
  lg: { container: "h-12 w-12", icon: "h-8 w-8", height: "h-8" },
  xl: { container: "h-16 w-16", icon: "h-10 w-10", height: "h-10" },
};

/**
 * FranklinLogo - Reusable atom component for the Franklin AI assistant logo.
 *
 * Displays the Franklin logo in a circular container with consistent styling.
 * Supports multiple sizes and optional click handling for use in different contexts.
 *
 * Visual specs:
 * - Container: Rounded full (circular), white background
 * - Border: 1px solid #ffd6a7 (orange accent) - optional
 * - Shadow: sm - optional
 * - Logo: Centered within container
 *
 * @example
 * // Basic usage
 * <FranklinLogo />
 *
 * @example
 * // With click handler
 * <FranklinLogo onClick={() => openChat()} size="lg" />
 *
 * @example
 * // Small size without border
 * <FranklinLogo size="xs" showBorder={false} />
 */
export function FranklinLogo({
  size = "md",
  onClick,
  className,
  showBorder = true,
  showShadow = false,
  borderColor = "#ffd6a7",
  imageClassName,
  animated = false,
  expanded = false,
  alt = "Franklin",
}: FranklinLogoProps) {
  const { container, icon, height } = sizeConfig[size];
  // An empty alt already keeps the image out of the accessibility tree; the
  // attribute states the intent for anything reading the DOM instead.
  const decorative = alt === "" || undefined;

  if (expanded) {
    // Render the official Franklin wordmark SVG (mark + "Franklin" text) so
    // the wordmark's brand font is preserved exactly. Height is locked to the
    // size preset's container height so the mark portion lines up visually
    // with the standalone mark used in the collapsed state.
    const outerClasses = cn(
      "flex items-center bg-transparent",
      onClick && "cursor-pointer transition-opacity hover:opacity-80",
      className
    );
    const imgClasses = cn(height, "w-auto object-contain", imageClassName);
    const wordmark = (
      <img src={WORDMARK_LOGO_SRC} alt={alt} aria-hidden={decorative} className={imgClasses} />
    );

    if (onClick) {
      return (
        <button type="button" onClick={onClick} className={outerClasses} aria-label="Franklin">
          {wordmark}
        </button>
      );
    }
    return <div className={outerClasses}>{wordmark}</div>;
  }

  // Standalone mark variant (collapsed / chatbot / loading states).
  const containerClasses = cn(
    "flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-white",
    showBorder && "border",
    showShadow && "shadow-sm",
    onClick && "cursor-pointer transition-transform hover:scale-105",
    container,
    className
  );
  const containerStyle = showBorder ? { borderColor } : undefined;
  const mediaClassName = cn("object-contain", icon, imageClassName);
  const mark = (
    <img
      src={animated ? LOADING_ANIMATION_SRC : STATIC_LOGO_SRC}
      alt={alt}
      aria-hidden={decorative}
      className={mediaClassName}
    />
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={containerClasses}
        style={containerStyle}
        aria-label="Franklin"
      >
        {mark}
      </button>
    );
  }

  return (
    <div className={containerClasses} style={containerStyle}>
      {mark}
    </div>
  );
}

FranklinLogo.displayName = "FranklinLogo";
